import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  styled,
  css,
  useTheme,
  Button,
  Tooltip,
  Divider,
} from '@mui/material'
import { Dialog } from '@material-ui/core'
import { Close } from '@mui/icons-material'
import { decodeTrademarkEntity } from '@helpers/string-helper'
import { useTest, TEST_ID_CATEGORIES } from '@hooks/useTest'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import AddToCartCheckmarkYMAL from '@components/product/product-parts/add-to-cart-checkmark-ymal'

import { useProduct } from '@components/product/hooks'
import ShopAndDeliverTo from '@components/shared/ShopAndDeliverTo'
import StrapiLink from '@templates/strapi-cms/content-types/Link'

import PriceAtom from '../../atoms/ProductPrice/PriceAtom'
import SaleFlag from '../../atoms/SaleFlag/SaleFlag'
import ProductPrice from '../../atoms/ProductPrice/ProductTilePrice'

import FavoriteButton from '../../atoms/Button/Favorite'
import ProductColor from '../../atoms/ProductColor'
import ProductTileImage from './ProductTileImage'
import Ratings from '../../atoms/Ratings/Ratings'

const StyledCard = styled(Card)`
  // TODO: Remove, this is from global sass files missing in storybooks
  .hide508 {
    clip: rect(1px 1px 1px 1px) !important;
    clip: rect(1px, 1px, 1px, 1px) !important;
    height: 1px !important;
    overflow: hidden !important;
    position: absolute !important;
    white-space: nowrap !important;
    width: 1px !important;
  }

  // Remove android blue outline
  -webkit-tap-highlight-color: transparent;
`

const StyledProductTileButton = styled(Button)`
  font-size: 1rem;
`
const StyledAddToCartButton = styled(Button)(({ theme }) => ({
  width: '85%',
  lineHeight: 1.875,
  fontSize: '1rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}))

const StyledTypography = styled(Typography)`
  .free-shipping-tag {
    color: ${({ theme }) => theme.palette.error.main};
  }
`

const ProductTileButtonWrapper = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
`

const AddToCartYMALButtonWrapper = styled('div')`
  height: 2.5rem;
  display: flex;
  width: 100%;
  position: absolute;
  bottom: 0;
  display: flex;
  justify-content: center;
`
const StyledRatingsContainer = styled(Stack)(
  ({ theme }) => css`
    align-items: end;
    margin-bottom: 9px;
    .MuiRating-root {
      font-size: 18px;
      width: auto;
      .MuiRating-iconFilled {
        color: ${theme.palette.primary.main} !important;
      }
    }
    .ProductTileReviewsCount {
      font-size: 12px;
      line-height: 16px;
      color: ${theme.palette.secondary.lighter};
    }
  `,
)

const ShopCollectionButtonStyles = styled('div')`
  width: 100%;
`

const LearnMoreButton = styled('button')`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-decoration: underline;
  color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
`

const LearnMorePromoFeeContent = styled(Box)`
  display: grid;
  overflow: auto;
  gap: 1rem;
  position: relative;
  padding-top: 40px;

  .dialog-close-btn {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
  }

  .grid {
    display: grid;
  }
  .flex {
    display: flex;
  }
  .flex-col {
    flex-direction: column;
  }
  .gap-2px {
    gap: 2px;
  }
  .gap-4 {
    gap: 1rem;
  }
  .flex-1 {
    flex: 1 1 0%;
  }
  .items-center {
    align-items: center;
  }
  .rounded {
    border-radius: 0.25rem /* 4px */;
  }
  .p-2 {
    padding: 0.5rem /* 8px */;
  }
  .min-w-150px {
    min-width: 150px;
  }
  .inline-block {
    display: inline-block;
  }
  .align-middle {
    vertical-align: middle;
  }
  .text-50-p {
    font-size: 50%;
  }
  .mx-auto {
    margin-left: auto;
    margin-right: auto;
  }
  .w-full {
    width: 100%;
  }
  .max-w-250px {
    max-width: 250px;
  }

  .bg-roomy-primary-blue-5 {
    background-color: #0053a00d;
  }
  .bg-roomy-primary-blue-10 {
    background-color: #0053a01a;
  }

  .text-roomy-primary-dark-blue {
    --tw-text-opacity: 1;
    color: rgb(7 38 59 / var(--tw-text-opacity)) /* #07263b */;
  }
  .text-primary-dark {
    --tw-text-opacity: 1;
    color: rgb(7 38 59 / var(--tw-text-opacity)) /* #07263b */;
  }

  .heading-3 {
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: capitalize;
    color: #333333;

    @media screen and (min-width: 1024px) {
      font-size: 25px;
    }
  }
  .paragraph-1 {
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #333333;
  }
  .paragraph-2 {
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    color: #333333;
  }

  .md-flex-row {
    @media (min-width: 768px) {
      flex-direction: row;
    }
  }
  .md-min-h-\[50px\] {
    @media (min-width: 768px) {
      min-height: 50px;
    }
  }
  .md-rounded-r-none {
    @media (min-width: 768px) {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    }
  }
  .md-justify-center {
    @media (min-width: 768px) {
      justify-content: center;
    }
  }
  .md-rounded-l-none {
    @media (min-width: 768px) {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  }

  sup {
    position: relative;
    top: -0.45em;
  }

  b {
    font-weight: 600;
  }
`

export default function ProductTile({
  primary_image,
  alternate_image,
  type,
  deliveryType,
  productCategory,
  subCategories,
  brand,
  feature,
  title,
  sku,
  price,
  handleClick,
  sale,
  displaySaleFlag,
  financeAmount,
  financeNumMonths,
  promotionalPrice,
  promotionalMonths,
  displayPromotionalFinance = false,
  availableColorVariations,
  isChecked,
  room_image,
  isLoading,
  isError,
  updateFavorite,
  route,
  updateVariant,
  inViewRef,
  showVariations = true,
  strikeThroughPrice = 0,
  strikeThrough = false,
  saleFlagLabel,
  productExtraLabel,
  isDropShipping,
  isFreeShipping,
  isAvailableForRegion,
  onAddToCart,
  renderAddToCartModal,
  trackingData,
  source,
  product = null,
  isInStock: forceIsInStock,
  showAddToCart: forceShowAddToCart,
  rating = { reviews: null, averageRating: null, ratings: null },
  sectionalExtraLabel,
  showFinance = false,
  showFinancePromoFee = false,
}) {
  const {
    routeToSimilarItems,
    isInStock,
    showAddToCart,
    isThankyouDiscount,
    isMattressOnly,
    promoMessageHTML,
  } = useProduct({
    product,
  })

  const [YMALButtonClicked, setYMALButtonClicked] = useState(false)
  const [isTooltipHover, setIsTooltipHover] = useState(false)

  const theme = useTheme()
  const { generateTestId } = useTest()
  const isYMAL = source === 'add-to-cart-modal'
  const showAddToCartOnPLP = forceShowAddToCart || showAddToCart

  const [variantLimit, setVariantLimit] = useState(isYMAL ? 4 : 5)

  const onUpdateVariant = selectedSku => updateVariant(selectedSku)

  const includesSunbrella = feature?.includes('Sunbrella')
  const availableColorsForRegion = availableColorVariations
  const isFavoriteUnavailable = !showAddToCartOnPLP && source === 'favorites'
  const isSectionalOrRoomSet = type === 'room'
  const isSectional = subCategories?.find(subCategory => subCategory === 'Sectionals')

  const onFavoriteButtonClick = e => {
    updateFavorite({ remove: isChecked })
  }

  const onAddToCartButton = e => {
    e.preventDefault()
    setYMALButtonClicked(true)
    onAddToCart(e)
  }

  const onShopSimilarProducts = e => {
    e.stopPropagation()
    e.preventDefault()
    routeToSimilarItems()
  }

  const [isLearnMoreFeeOpen, setIsLearnMoreFeeOpen] = useState(false)

  const closeLearnMoreFeeModal = () => {
    setIsLearnMoreFeeOpen(false)
  }

  const openLearnMoreFeeModal = () => {
    setIsLearnMoreFeeOpen(true)

    addToDataLayer_nextgen('financing_learn_more')
  }

  const priceWithPromoFee = useMemo(() => price || 0, [price])
  const [productDollars, productCents] = priceWithPromoFee.toFixed(2).split('.')

  const YMALButton = () =>
    !YMALButtonClicked ? (
      <StyledAddToCartButton
        onClick={onAddToCartButton}
        color="primary"
        fullWidth
        variant="outlined"
        aria-label={`Add To Cart for ${title}`}
      >
        Add to Cart
      </StyledAddToCartButton>
    ) : (
      <AddToCartCheckmarkYMAL />
    )

  useEffect(() => {
    const handleResize = () =>
      setVariantLimit(() => {
        let value = 5

        if (window.innerWidth < 748) value = 5
        else if (window.innerWidth < 940) value = 3
        else if (window.innerWidth < 1024) value = 4
        else if (window.innerWidth < 1080) value = 2
        else if (window.innerWidth < 1280) value = 3
        else if (window.innerWidth < 1160) value = 4

        return isYMAL ? value - 1 : value
      })
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [isYMAL, setVariantLimit])

  const displayButton = () => {
    if (source === 'search' || source === 'strapi-slider') {
      if (isSectionalOrRoomSet) {
        return (
          <StyledProductTileButton
            color="primary"
            fullWidth
            variant="contained"
            aria-label={`Go to the Product Detail ${title}`}
          >
            Shop Collection
          </StyledProductTileButton>
        )
      }
      if (deliveryType === 'O') {
        return (
          <StyledProductTileButton
            onClick={onAddToCartButton}
            color="primary"
            fullWidth
            variant="contained"
            aria-label={`Add To Cart for ${title}`}
          >
            ADD TO CART
          </StyledProductTileButton>
        )
      }
      if (isMattressOnly) {
        return (
          <ShopCollectionButtonStyles
            className="shop-collection-button"
            data={{
              InternalUrl: route,
              testId: generateTestId(TEST_ID_CATEGORIES.PRODUCT_TITLE, sku),
              ...(!isTooltipHover && { Title: title }),
            }}
            trackingData={trackingData}
            disableUnderline
          >
            <StyledProductTileButton
              color="primary"
              fullWidth
              variant="contained"
              aria-label={`Go to the Product Detail ${title}`}
            >
              Shop Now
            </StyledProductTileButton>
          </ShopCollectionButtonStyles>
        )
      }
      return (
        <StyledProductTileButton
          color="primary"
          fullWidth
          variant="contained"
          aria-label={`Go to the Product Detail ${title}`}
        >
          Shop Now
        </StyledProductTileButton>
      )
    }
    return null
  }

  if (isLoading || !sku)
    return (
      <Stack direction="column" ref={inViewRef}>
        <Skeleton width="100%" sx={{ aspectRatio: '3/2' }} />
        <Box sx={{ pt: 0.5 }}>
          <Skeleton />
          <Skeleton width="60%" />
        </Box>
      </Stack>
    )
  // TODO: lets check our Algolia data here and make the UI updates
  return (
    <StrapiLink
      className="product-image-link"
      data={{
        InternalUrl: route,
        testId: generateTestId(TEST_ID_CATEGORIES.PRODUCT_TITLE, sku),
        ...(!isTooltipHover && { Title: title }),
      }}
      trackingData={trackingData}
      disableUnderline
    >
      <StyledCard
        square
        variant="outlined"
        sx={{
          overflow: 'visible',
          width: '100%',
          position: 'relative',
          height: '100%',
          border: 'none',
          ...(displaySaleFlag && saleFlagLabel === 'BONUS BUY' ? { border: '3px dashed #07263b' } : {}),
          paddingRight: '0',
        }}
        ref={inViewRef}
      >
        {isError && <div>Error {JSON.stringify(isError)}</div>}
        {displaySaleFlag && <SaleFlag label={saleFlagLabel} />}
        {renderAddToCartModal()}
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Product Images */}
          <ProductTileImage
            onClick={handleClick}
            primaryImage={type === 'room' && room_image ? room_image : primary_image}
            alternateImage={alternate_image}
            title={title}
            showAddToCartIcon={isAvailableForRegion && productCategory !== 'mattress bedding'}
            onAddToCartClick={onAddToCart}
          />
          <CardContent
            sx={{
              flex: 1,
              justifyContent: 'space-between',
              display: isYMAL ? 'block' : 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: isYMAL ? '16px 0 24px 0' : '16px',
            }}
          >
            <div>
              {/* Variants */}
              {availableColorsForRegion?.length > 0 && showVariations && (
                <Box display="flex" mb={isYMAL ? '10px' : '20px'} sx={{ gap: '4px' }} alignItems="center">
                  {availableColorsForRegion?.map(
                    (variant, key) =>
                      key < variantLimit &&
                      variant.variation_value !== '' && (
                        <ProductColor
                          disableEventPropagation
                          key={`color-variant-${variant.sku}`}
                          sx={{
                            marginRight: '5px',
                          }}
                          width={isYMAL ? '30px' : '44px'}
                          height={isYMAL ? '30px' : '44px'}
                          isSelected={sku === variant.sku}
                          color={variant}
                          onClick={e => onUpdateVariant(e)}
                        />
                      ),
                  )}
                  {availableColorsForRegion?.length > variantLimit && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width={isYMAL ? '34px' : '38px'}
                      height={isYMAL ? '34px' : '38px'}
                      minWidth={isYMAL ? '34px' : '38px'}
                      borderRadius="19px"
                      border="1px solid"
                      borderColor={theme.palette.secondary.main}
                      marginLeft="3px"
                    >
                      <Typography
                        fontSize={isYMAL ? '14px' : '16px'}
                        fontWeight="600"
                        color={theme.palette.secondary.lighter}
                      >
                        +{availableColorsForRegion?.length - variantLimit}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {(forceIsInStock || isInStock) && !isYMAL && (
                <Typography
                  color={theme.palette.success.main}
                  marginBottom="8px"
                  data-testid={generateTestId('product-tile', 'instock-label')}
                >
                  IN STOCK
                </Typography>
              )}
              {isFavoriteUnavailable && !isMattressOnly && (
                <Typography
                  color={theme.palette.secondary.dark}
                  marginBottom="8px"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  UNAVAILABLE IN: <ShopAndDeliverTo disablePreviousText bottom={false} variant="plp" />
                </Typography>
              )}
              {/* Product Brand */}
              {!isYMAL && (
                <Stack
                  direction="row"
                  sx={{
                    marginBottom: '8px',
                  }}
                >
                  {brand && (
                    <Typography
                      fontSize={isYMAL && '14px'}
                      variant="body1"
                      component="h2"
                      lineHeight="24px"
                      textTransform="none"
                      color={theme.palette.primary.main}
                      minWidth="117px"
                    >
                      {brand}
                    </Typography>
                  )}
                  {feature && brand && includesSunbrella && (
                    <Divider
                      orientation="vertical"
                      sx={{
                        width: '5px',
                        textAlign: 'center',
                        mx: '8px',
                      }}
                      flexItem
                    />
                  )}
                  {feature && includesSunbrella && (
                    <Typography
                      fontSize={isYMAL && '14px'}
                      variant="body1"
                      component="h2"
                      lineHeight="24px"
                      textTransform="none"
                      margin="none"
                      color={theme.palette.primary.main}
                    >
                      Sunbrella
                    </Typography>
                  )}
                </Stack>
              )}
              {/* Product Title */}
              {title && (
                <Typography
                  fontSize={isYMAL && '14px'}
                  variant="body1"
                  component="h2"
                  lineHeight="24px"
                  fontWeight="600"
                  marginBottom="8px"
                  textTransform="none"
                  color={theme.palette.primary.dark}
                  title={title}
                  data-testid={generateTestId('product-tile', 'title')}
                >
                  {decodeTrademarkEntity(title)}
                </Typography>
              )}
              {/* Product Price */}
              {isAvailableForRegion && (
                <ProductPrice
                  dataTestId={generateTestId('product-tile', 'price')}
                  fontSize={isYMAL ? '14px' : '23px'}
                  price={price}
                  sale={sale}
                  strikeThrough={strikeThrough}
                  strikeThroughPrice={strikeThroughPrice}
                  highlightPrice={saleFlagLabel?.toLowerCase() === 'last chance' || isThankyouDiscount}
                />
              )}

              {/* Product Financing Message */}
              {/* Wrap in Growthbook Feature */}
              {showFinance && financeAmount >= 5 && (
                <Typography
                  variant="body1"
                  color={theme.palette.error.main}
                  marginBottom="8px"
                  fontSize={isYMAL ? '12px' : '16px'}
                  data-testid={generateTestId('product-tile', 'finance-calculator')}
                >
                  {promotionalPrice && promotionalMonths && displayPromotionalFinance && (
                    <>
                      <span>
                        <PriceAtom price={promotionalPrice} suppressCentsIfZero />
                        <span aria-hidden="true">{` per Month First ${promotionalMonths} Months`}</span>
                      </span>
                      <br />
                      {'Then '}
                    </>
                  )}
                  <Tooltip
                    followCursor
                    onMouseEnter={() => setIsTooltipHover(true)}
                    onMouseLeave={() => setIsTooltipHover(false)}
                    title={
                      promotionalPrice && promotionalMonths
                        ? `$${promotionalPrice} per Month First ${promotionalMonths} Months`
                        : ''
                    }
                    placement="right-start"
                  >
                    <span>
                      <PriceAtom price={financeAmount} suppressCentsIfZero />
                      <span aria-hidden="true">/mo.</span>
                      <span class="hide508">/month</span>
                      {` with ${financeNumMonths} months financing*`}
                    </span>
                  </Tooltip>
                  {showFinancePromoFee && (
                    <>
                      {' '}
                      <LearnMoreButton
                        type="button"
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                          openLearnMoreFeeModal()
                        }}
                      >
                        Learn More
                      </LearnMoreButton>
                      <Dialog
                        open={isLearnMoreFeeOpen}
                        onClose={closeLearnMoreFeeModal}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                      >
                        <LearnMorePromoFeeContent p={3}>
                          <button className="dialog-close-btn" type="button" onClick={closeLearnMoreFeeModal}>
                            <Close />
                          </button>
                          <div
                            className="paragraph-1 grid gap-4"
                            dangerouslySetInnerHTML={{ __html: promoMessageHTML }}
                          />
                          <Button
                            variant="contained"
                            onClick={closeLearnMoreFeeModal}
                            sx={{
                              margin: 'auto',
                              maxWidth: '250px',
                              width: '100%',
                            }}
                          >
                            Close
                          </Button>
                        </LearnMorePromoFeeContent>
                      </Dialog>
                    </>
                  )}
                </Typography>
              )}

              {/* Product Rating */}
              {rating?.averageRating >= 0 && rating?.ratings >= 0 ? (
                <StyledRatingsContainer direction="row" spacing={1}>
                  <Ratings
                    name="product-tile-rating-read"
                    readOnly
                    max={5}
                    precision={0.2}
                    value={rating.averageRating}
                  />{' '}
                  <span className="ProductTileReviewsCount">[{rating?.ratings}]</span>
                </StyledRatingsContainer>
              ) : null}

              {/* Product Extras and Size */}
              {productExtraLabel && !isSectional && (
                <StyledTypography
                  variant="body1"
                  fontSize={isYMAL ? '12px' : '16px'}
                  marginBottom="8px"
                  color={productExtraLabel === 'Free Shipping' ? theme.palette.error.main : theme.palette.primary.main}
                  dangerouslySetInnerHTML={{ __html: productExtraLabel }}
                />
              )}
              {isSectional && source === 'search' && sectionalExtraLabel && (
                <StyledTypography
                  variant="body1"
                  fontSize={isYMAL ? '12px' : '16px'}
                  marginBottom="8px"
                  color={theme.palette.primary.main}
                >
                  {sectionalExtraLabel}
                </StyledTypography>
              )}
              {isFreeShipping && isDropShipping && (
                <Typography
                  variant="body1"
                  fontSize={isYMAL ? '14px' : '16px'}
                  marginBottom="8px"
                  color={theme.palette.error.main}
                >
                  Free Shipping
                </Typography>
              )}
            </div>
            {showAddToCartOnPLP && !isMattressOnly && isYMAL ? (
              <AddToCartYMALButtonWrapper>
                <YMALButton />
              </AddToCartYMALButtonWrapper>
            ) : (
              showAddToCartOnPLP && <ProductTileButtonWrapper>{displayButton()}</ProductTileButtonWrapper>
            )}
            {isFavoriteUnavailable && !isMattressOnly && (
              <ProductTileButtonWrapper>
                <StyledProductTileButton
                  onClick={onShopSimilarProducts}
                  color="primary"
                  fullWidth
                  data-testid={generateTestId(TEST_ID_CATEGORIES.ADD_TO_CART_BUTTON, sku)}
                  variant="outlined"
                  aria-label={`Shop similar products for ${title}`}
                >
                  SHOP SIMILAR PRODUCTS
                </StyledProductTileButton>
              </ProductTileButtonWrapper>
            )}
          </CardContent>
        </Box>
        <CardActions
          className="favoriteButtonContainer"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: isYMAL ? '5px' : '8px',
          }}
        >
          <FavoriteButton
            sku={sku}
            checked={isChecked}
            handleClick={onFavoriteButtonClick}
            size={isYMAL ? 'small' : null}
          />
        </CardActions>
      </StyledCard>
    </StrapiLink>
  )
}

ProductTile.propTypes = {
  primary_image: PropTypes.string,
  alternate_image: PropTypes.string,
  room_image: PropTypes.string,
  route: PropTypes.string,
  type: PropTypes.string,
  deliveryType: PropTypes.string,
  productCategory: PropTypes.string,
  subCategories: PropTypes.array,
  brand: PropTypes.string,
  feature: PropTypes.string,
  title: PropTypes.string,
  sku: PropTypes.string,
  price: PropTypes.number,
  strikeThroughPrice: PropTypes.number,
  strikeThrough: PropTypes.bool,
  sale: PropTypes.bool,
  displaySaleFlag: PropTypes.bool,
  financeAmount: PropTypes.number,
  financeNumMonths: PropTypes.number,
  promotionalPrice: PropTypes.number,
  promotionalMonths: PropTypes.number,
  displayPromotionalFinance: PropTypes.bool,
  availableColorVariations: PropTypes.array,
  handleClick: PropTypes.func,
  updateVariant: PropTypes.func,
  isChecked: PropTypes.bool,
  inViewRef: PropTypes.func.isRequired /** Required for InView logic of useProductQuery */,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  updateFavorite: PropTypes.func,
  showVariations: PropTypes.bool,
  saleFlagLabel: PropTypes.string,
  productExtraLabel: PropTypes.string,
  isDropShipping: PropTypes.bool,
  isFreeShipping: PropTypes.bool,
  isAvailableForRegion: PropTypes.bool,
  onAddToCart: PropTypes.func,
  renderAddToCartModal: PropTypes.func,
  trackingData: PropTypes.object,
  source: PropTypes.string,
  product: PropTypes.object,
  isInStock: PropTypes.bool,
  showAddToCart: PropTypes.bool,
  rating: PropTypes.shape({
    reviews: PropTypes.number,
    averageRating: PropTypes.number,
    ratings: PropTypes.number,
  }),
  sectionalExtraLabel: PropTypes.string,
  showFinance: PropTypes.bool,
  showFinancePromoFee: PropTypes.bool,
}
